import {
  collection,
  FirestoreDataConverter,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { useMemo } from 'react';
import { eachDayOfInterval, format } from 'date-fns';
import { firestore } from '@/lib/firebase';

export type BoxStatusValue =
  | 'ONLINE'
  | 'COMMAND_FAIL'
  | 'PING_FAIL'
  | 'UNKNOWN';

export interface BoxStatusEvent {
  ip: string;
  name: string;
  status: BoxStatusValue;
  timestamp: Date;
}

const converter: FirestoreDataConverter<BoxStatusEvent> = {
  toFirestore: () => {
    throw new Error('boxStatusHistory is read-only from the client');
  },
  fromFirestore: (snapshot) => {
    const d = snapshot.data();
    return {
      ip: d.ip,
      name: d.name,
      status: d.status,
      timestamp: d.timestamp?.toDate?.() ?? new Date(0),
    };
  },
};

/** Hard cap on history docs per query. The agent writes a sample per box every
 *  5 minutes, so a wide date range over many boxes can reach hundreds of
 *  thousands of docs — enough to freeze the tab (and cost real reads). When the
 *  cap is hit the newest samples win; the UI shows a truncation warning. */
export const HISTORY_QUERY_LIMIT = 50_000;

/** Live status samples in [start, end], newest first, capped at
 *  HISTORY_QUERY_LIMIT. Only filters/sorts by timestamp, so the default
 *  single-field index is enough (no composite index required). Consumers sort
 *  per box themselves, so the descending order doesn't matter to them. */
export function useBoxStatusHistory(start: Date, end: Date) {
  const historyQuery = useMemo(
    () =>
      query(
        collection(firestore, 'boxStatusHistory').withConverter(converter),
        where('timestamp', '>=', Timestamp.fromDate(start)),
        where('timestamp', '<=', Timestamp.fromDate(end)),
        orderBy('timestamp', 'desc'),
        limit(HISTORY_QUERY_LIMIT)
      ),
    [start, end]
  );

  return useCollectionData(historyQuery, {
    snapshotListenOptions: { includeMetadataChanges: false },
  });
}

export interface BoxMetrics {
  total: number;
  online: number;
  commandFail: number;
  pingFail: number;
  onlinePct: number;
  commandFailPct: number;
  pingFailPct: number;
  /** Percentage of samples not ONLINE (downtime). */
  failPct: number;
  /** Number of distinct failure runs. */
  incidents: number;
  /** Longest contiguous time in a failure state, in milliseconds. */
  longestOutageMs: number;
}

export interface BoxStat extends BoxMetrics {
  ip: string;
  name: string;
}

/** Compute metrics for one box's samples (must be sorted ascending by time). */
function computeMetrics(samples: BoxStatusEvent[]): BoxMetrics {
  let online = 0;
  let commandFail = 0;
  let pingFail = 0;
  let incidents = 0;
  let longestOutageMs = 0;
  let outageStart: number | null = null;

  for (const sample of samples) {
    if (sample.status === 'ONLINE') online++;
    else if (sample.status === 'COMMAND_FAIL') commandFail++;
    else if (sample.status === 'PING_FAIL') pingFail++;

    const isFailure =
      sample.status === 'COMMAND_FAIL' || sample.status === 'PING_FAIL';

    if (isFailure) {
      if (outageStart === null) {
        outageStart = sample.timestamp.getTime();
        incidents++;
      }
    } else if (sample.status === 'ONLINE' && outageStart !== null) {
      longestOutageMs = Math.max(
        longestOutageMs,
        sample.timestamp.getTime() - outageStart
      );
      outageStart = null;
    }
  }

  if (outageStart !== null && samples.length) {
    longestOutageMs = Math.max(
      longestOutageMs,
      samples[samples.length - 1].timestamp.getTime() - outageStart
    );
  }

  const total = samples.length;
  const pct = (n: number) => (total ? (n / total) * 100 : 0);
  const onlinePct = pct(online);

  return {
    total,
    online,
    commandFail,
    pingFail,
    onlinePct,
    commandFailPct: pct(commandFail),
    pingFailPct: pct(pingFail),
    failPct: total ? 100 - onlinePct : 0,
    incidents,
    longestOutageMs,
  };
}

function groupByIp(events: BoxStatusEvent[]): Map<string, BoxStatusEvent[]> {
  const byBox = new Map<string, BoxStatusEvent[]>();
  for (const event of events) {
    const list = byBox.get(event.ip);
    if (list) list.push(event);
    else byBox.set(event.ip, [event]);
  }
  return byBox;
}

/** Per-box stats aggregated over the whole window. */
export function aggregateStats(events: BoxStatusEvent[]): BoxStat[] {
  const stats: BoxStat[] = [];
  for (const [ip, samples] of groupByIp(events)) {
    samples.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    stats.push({ ip, name: samples[0].name, ...computeMetrics(samples) });
  }
  stats.sort((a, b) => a.name.localeCompare(b.name));
  return stats;
}

export interface DailyRow {
  ip: string;
  name: string;
  overall: BoxMetrics;
  perDay: Record<string, BoxMetrics>;
}

export interface DailyStats {
  /** Day keys (yyyy-MM-dd) spanning the window, in order. */
  days: string[];
  rows: DailyRow[];
}

/** Per-box, per-day breakdown for spotting patterns. Rows are sorted worst-first
 *  (highest overall downtime). */
export function aggregateByDay(
  events: BoxStatusEvent[],
  start: Date,
  end: Date
): DailyStats {
  const days = eachDayOfInterval({ start, end }).map((d) =>
    format(d, 'yyyy-MM-dd')
  );

  const rows: DailyRow[] = [];
  for (const [ip, samples] of groupByIp(events)) {
    samples.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    const buckets: Record<string, BoxStatusEvent[]> = {};
    for (const sample of samples) {
      const key = format(sample.timestamp, 'yyyy-MM-dd');
      (buckets[key] ||= []).push(sample);
    }

    const perDay: Record<string, BoxMetrics> = {};
    for (const key of Object.keys(buckets)) {
      perDay[key] = computeMetrics(buckets[key]);
    }

    rows.push({
      ip,
      name: samples[0].name,
      overall: computeMetrics(samples),
      perDay,
    });
  }

  rows.sort(
    (a, b) => b.overall.failPct - a.overall.failPct || a.name.localeCompare(b.name)
  );

  return { days, rows };
}
